import React, { useEffect, useState, useRef } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { Container, Box, Button, Stepper, Step, StepLabel, Typography, TextField, MenuItem, FormControl, RadioGroup, FormLabel, FormControlLabel, Radio, Tooltip, Divider, ToggleButton, ToggleButtonGroup, Alert } from '@mui/material';
import { StandardInput, PercentInput } from '../components/StandardInput';
import { AddressAutocomplete } from '../components/AddressAutocomplete';
import { FormRow, ContentCard } from '../components/StandardLayout';
import { colors } from '../theme';
import { useRole, useUser, useUserLoaded } from '../context/UserContext';
import UnitTable from '../components/UnitTable';
import UnitMixTable from '../components/UnitMixTable';
import RentRollImport from '../components/RentRollImport';
import { reDeriveVacateMonths } from '../utils/rentRollImport';
import OperatingExpensesTable from '../components/OperatingExpenses';
import AmenityIncomeTable from '../components/AmentityIncome';
import { BACKEND_URL } from '../utils/constants';
import ModelIntroSteps from '../components/ModelIntroSteps';
import ModelStepper from '../components/ModelStepperNew';
import InfoOutlineIcon from '@mui/icons-material/InfoOutline';
import { SectionFields } from '../components/SectionFields';
import RetailIncomeTable from '../components/RetailIncomeTable';
import RefinancingSection from '../components/RefinancingSection';
import SeniorConstructionLoanSection from '../components/SeniorConstructionLoanSection';
import SecondLienSection from '../components/SecondLienSection';
import { AmenityIncomeBasic, ExpensesBasic, ExpensesBasicDevelopment, ExpensesIndustrial, GrowthRatesBasic, GrowthRatesDevelopment, MarketRentAssumptionsBasic, OperatingExpensesBasic, OperatingExpensesBasicDevelopment, FIELD_TITLE_CONSTANTS } from '../utils/newModelConstants';
import { Expenses } from '../components/Expenses';
import { Expense } from '../utils/interface';
import { resolveBuildableSqFt } from '../utils/projectCostCalcs';
import AcquisitionFinancingSection from '../components/AcquisitionFinancingSection';
import { RetailExpenses } from '../components/RetailExpenses';
import { NumberInput } from '../components/NumberInput';
import LeasingAssumptions from '../components/LeasingAssumptions';
import RecoveryIncomeTable from '../components/RecoveryIncomeTable';
import GrossPotentialRetailIncomeTable from '../components/GrossPotentialRetailIncomeTable';
import LeasingCostReserves from '../components/LeasingCostReserves';
import ExitAssumptions from '../components/ExitAssumptions';
import { NetOperatingIncomeTable } from '../components/NetOperatingIncomeTable';
import RecoveryIncomeTableIndustrial from '../components/RecoveryIncomeTableIndustrial';
import { RetailExpensesIndustrial } from '../components/RetailExpensesIndustrial';
import GrossPotentialRetailIncomeTableIndustrial from '../components/GrossPotentialRetailIncomeTableIndustrial';
import { blockingFieldLabels, isSectionComplete, visibleFields } from '../utils/sectionCompletion';
import RetailSummary from '../components/RetailSummary';
import { useLayout } from '../context/LayoutContext';
import { DevelopmentRentalAssumptions, DevelopmentUnitRow } from '../components/DevelopmentRentalAssumptions';
import DevelopmentRefinancingSection from '../components/DevelopmentRefinancingSection';

type Section = {
  id: string;
  name: string;
  order: number;
  fields: { id: string; name: string; default_value: string; required: boolean; field_key: string; field_title: string; field_type: string; order: number; }[];
};

interface ModelTypeInfo {
  show_retail: boolean;
  show_rental_units?: boolean;
  development_model?: boolean;
  sections: {
    id: string;
    name: string;
    order: number;
    fields: Field[];
  }[];
}

type Field = {
  id: string;
  name: string;
  default_value: string;
  required: boolean;
  field_key: string;
  field_title: string;
  field_type: string;
  order: number;
  time_phased: boolean;
  description: string;
  active: boolean;
};

interface Unit {
  id: string;
  rent_type: string;
  vacate_flag: number;
  layout: string;
  square_feet: number;
  vacate_month: number;
  current_rent: number | null;
  // Populated by a rent-roll import; null everywhere else. These round-trip
  // through the API already and must stay identical to the Unit shapes in
  // UnitTable / UnitMixTable, or the setUnits dispatch types stop matching.
  unit_label?: string | null;
  lease_end?: string | null;
  market_rent_override?: number | null;
  is_section8?: boolean | null;
  import_source?: string | null;
}

interface AmenityIncome {
  id: string;
  name: string;
  start_month: number;
  utilization: number;
  unit_count: number;
  monthly_fee: number;
}

interface OperatingExpense {
  id: string;
  name: string;
  factor: number;
  broker: number;
  cost_per: string;
}

interface RetailIncome {
  id: string;
  suite: string;
  tenant_name: string;
  square_feet: number;
  rent_start_month: number;
  annual_bumps: number;
  rent_per_square_foot_per_year: number;
  lease_start_month: number;
  recovery_start_month: number;
}

interface CreateModelProps {
  existingModel?: boolean;
  modelId?: string;
}

const RetailSectionHeader = ({ title, description }: { title: string; description?: string }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: { xs: "column", sm: "row" },
      alignItems: { xs: "flex-start", sm: "baseline" },
      gap: { xs: 0.5, sm: 2 },
      mb: 1.5,
    }}
  >
    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: colors.grey[900] }}>
      {title}
    </Typography>
    {description && (
      <Typography
        variant="body2"
        sx={{
          color: colors.grey[600],
          maxWidth: 520,
          lineHeight: 1.4,
        }}
      >
        {description}
      </Typography>
    )}
  </Box>
);


export const CreateModel = ({ existingModel, modelId }: CreateModelProps) => {
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const { user } = useUser();
  // Enterprise batch 8: a viewer may click through an existing deal's steps
  // but not change anything (inputs disabled below; the server refuses the
  // writes regardless), and cannot start a new deal.
  const { isViewer } = useRole();
  const userLoaded = useUserLoaded();
  const exitReadOnly = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate('/');
  };
  const [activeStep, setActiveStep] = useState(0);
  // const [selectedModelType, setSelectedModelType] = useState('');
  // const [introStepComplete, setIntroStepComplete] = useState(false);
  const [modelDetails, setModelDetails] = useState<Record<string, any>>({
    name: '',
    street_address: '',
    city: '',
    state: '',
    zip_code: '',
    latitude: null,
    longitude: null,
    user_model_field_values: [],
    market_rent_assumptions: [],
    development_units: []
  });
  const [modelTypes, setModelTypes] = useState<{ id: string; name: string; is_active: boolean, show_retail: boolean }[]>([]);
  const [selectedModelTypeInfo, setSelectedModelTypeInfo] = useState<ModelTypeInfo | null>(null);
  const [units, setUnits] = useState<Unit[]>([]);
  // Rent roll entry mode on the Residential Rental Units step: the classic
  // one-row-per-unit grid, the floor-plan mix generator, or an import of a
  // property-management rent roll export.
  const [rentRollMode, setRentRollMode] = useState<'unit' | 'mix' | 'import'>('unit');
  const [nameError, setNameError] = useState(false);
  const [rentalRateError] = useState(false);
  const [marketRentAssumptions, setMarketRentAssumptions] = useState<{ layout: string; pf_rent: number; square_feet?: number | null; }[]>(MarketRentAssumptionsBasic);
  const [growthRates, setGrowthRates] = useState<{ name: string; value: number; type: string; }[]>(GrowthRatesBasic);
  const [amenityIncome, setAmenityIncome] = useState<AmenityIncome[]>([]);
  const [retailIncome, setRetailIncome] = useState<RetailIncome[]>([]);
  const [operatingExpenses, setOperatingExpenses] = useState<OperatingExpense[]>([]);
  const [developmentUnits, setDevelopmentUnits] = useState<DevelopmentUnitRow[]>([]);
  const [isCreating, setIsCreating] = useState(false);
  const [newRentalGrowthName, setNewRentalGrowthName] = useState('');
  const [modelMapping, setModelMapping] = useState({});
  const [variableMapping, setVariableMapping] = useState({});
  const [leveredIrr, setLeveredIrr] = useState('');
  const [leveredMoic, setLeveredMoic] = useState('');
  const [variables, setVariables] = useState({});
  const [noiTableValues, setNoiTableValues] = useState<any[]>([]);
  const [finalMetricsCalculating, setFinalMetricsCalculating] = useState(false);
  const [finalMetricsCalculating2, setFinalMetricsCalculating2] = useState(false);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const { setHideMainSidebar, modelCreationStarted, setModelCreationStarted, selectedModelTypeId, setSelectedModelTypeId } = useLayout();
  const selectedModelType = selectedModelTypeId;
  const setSelectedModelType = setSelectedModelTypeId;

  const introStepComplete = modelCreationStarted || !!existingModel;
  // Set when loading an existing model fails, so the user sees why instead of a
  // blank form whose Save button never enables.
  const [loadError, setLoadError] = useState<string>('');
  const exitAssumptionsNumUnits = selectedModelTypeInfo?.development_model
    ? developmentUnits.reduce((sum, row) => sum + Number(row.units || 0), 0)
    : units.length;

  useEffect(() => {
    setHideMainSidebar(introStepComplete);
    // NOTE: Do NOT reset modelCreationStarted in cleanup - it must survive remounting
  }, [introStepComplete, setHideMainSidebar]);
  
  // Reset context state only when navigating away from create-model/edit-model page entirely
  useEffect(() => {
    return () => {
      // Use a small delay to check if we're still on create-model/edit-model page
      // If we remounted due to layout change, we'll still be on the same route
      // Note: Don't include 'models/' - that's the detail page which should show main sidebar
      setTimeout(() => {
        const stillOnModelCreationPage = window.location.pathname.includes('create-model') ||
                                          window.location.pathname.includes('edit-model');
        if (!stillOnModelCreationPage) {
          setHideMainSidebar(false);
          setModelCreationStarted(false);
          setSelectedModelTypeId('');
        }
      }, 50);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  // Add debounce timeout ref
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Keep latest readiness values in refs to avoid stale closures
  const modelMappingRef = useRef(modelMapping);
  useEffect(() => { modelMappingRef.current = modelMapping; }, [modelMapping]);
  const variableMappingRef = useRef(variableMapping);
  useEffect(() => { variableMappingRef.current = variableMapping; }, [variableMapping]);
  const googleUrlRef = useRef(modelDetails.google_sheet_url);
  useEffect(() => { googleUrlRef.current = modelDetails.google_sheet_url; }, [modelDetails.google_sheet_url]);


  const [steps, setSteps] = useState<string[]>([]);

  // Resolve space type ("Retail", "Industrial", etc.) from user field values
  const resolvedSpaceType = (modelDetails?.user_model_field_values || []).find((f: any) => {
    const k = String(f.field_key || '');
    return k === 'space_type' || k.trim() === 'space_type';
  })?.value ?? 'Retail';

  // Module-level variable to persist across renders and prevent duplicate calls
  const fetchModelDetailsRef = useRef<string | null>(null);
  const isFetchingRef = useRef(false);

  // Global flag to prevent duplicate calls (persists across component instances)
  if (!(window as any).__modelDetailsFetchFlag) {
    (window as any).__modelDetailsFetchFlag = new Set();
  }

  const isCreatingRef = useRef(isCreating);
  useEffect(() => {
    isCreatingRef.current = isCreating;
  }, [isCreating]);

  const clearGoogleSheetUrl = () => {
    googleUrlRef.current = '';
    (window as any).__generatedSheetUrl = '';
    setModelDetails(prevDetails => ({
      ...prevDetails,
      google_sheet_url: ''
    }));
  };

  const resolveGoogleSheetUrl = () => {
    const cachedUrl = (window as any).__generatedSheetUrl;
    const url = googleUrlRef.current || cachedUrl || modelDetails.google_sheet_url || '';
    if (url && !googleUrlRef.current) {
      googleUrlRef.current = url;
    }
    return url;
  };

  useEffect(() => {
    const cachedUrl = (window as any).__generatedSheetUrl;
    if (!modelDetails.google_sheet_url && cachedUrl) {
      googleUrlRef.current = cachedUrl;
      setModelDetails(prevDetails => ({
        ...prevDetails,
        google_sheet_url: cachedUrl
      }));
    }
  }, [modelDetails.google_sheet_url]);


  useEffect(() => {
    if(!selectedModelTypeInfo){
      setSteps(["Property Address"]);
      return;
    }

    if(selectedModelTypeInfo?.development_model) {

      
        if(!existingModel){
          setExpenses(ExpensesBasicDevelopment);
          setOperatingExpenses(prev => prev.length > 0 ? prev : OperatingExpensesBasicDevelopment);
        }

    
      setSteps(
        [
          "Property Address",
          "General Property Assumptions",
          "Rent Assumptions",
          "Retail Income",
          "Amenity Income",
          "Operating Expenses",
          "Hard Costs",
          "Net Operating Income",
          "Senior Construction Loan Financing",
          "Second Lien",
          "Refinancing",
          "Leasing Assumptions",
          "Closing Costs",
          "Legal and Pre-Development Costs",
          "Soft Costs",
          "Exit Assumptions"
        ]
      )
      // Default growth rates for development models (do not override existing model pulls)
      if (!existingModel) {
        setGrowthRates(GrowthRatesDevelopment);
      }
    }
    else if(selectedModelTypeInfo?.show_rental_units && !selectedModelTypeInfo?.show_retail) {

      if(!existingModel){
        setExpenses(ExpensesBasic);
        setOperatingExpenses(prev => prev.length > 0 ? prev : OperatingExpensesBasic);
      }
      setSteps(
        [
          "Property Address",
          "General Property Assumptions",
          "Residential Rental Units",
          "Market Rent Assumptions",
          // "Retail Income",
          "Amenity Income",
          "Operating Expenses",
          "Net Operating Income",
          "Acquisition Financing",
          "Leasing Assumptions",
          "Refinancing",
          ...EXPENSE_STEPS,
          "Exit Assumptions"
        ]
      )
      if (!existingModel) {
        setGrowthRates(GrowthRatesBasic);
      }
    }
    else if (selectedModelTypeInfo?.show_retail && !selectedModelTypeInfo?.development_model) {
      if(selectedModelTypeInfo?.show_rental_units) {
        
        if(!existingModel){
          setExpenses(ExpensesBasic);
          setOperatingExpenses(prev => prev.length > 0 ? prev : OperatingExpensesBasic);
        }
        setSteps(
          [
            "Property Address",
            "General Property Assumptions",
            "Residential Rental Units",
            "Market Rent Assumptions",
            "Retail Income",
            "Amenity Income",
            "Operating Expenses",
            "Net Operating Income",
            "Acquisition Financing",
            "Leasing Assumptions",
            "Refinancing",
            ...EXPENSE_STEPS,
            "Exit Assumptions"
          ]
        )
        if (!existingModel) {
          setGrowthRates(GrowthRatesBasic);
        }
      }
      else {
        if(!existingModel){
          setExpenses(ExpensesIndustrial);
        }
        setSteps(
          [
            "Property Address",
            "General Property Assumptions",
            // "Retail Income",
            "Base Income",
            "Recoverable Operating Expenses",
            "Recovery and Gross Potential Income",
            "TILC Reserves",
            "Income Summary",
            "Amenity Income",
            "Net Operating Income",
            "Acquisition Financing",
            "Refinancing",
            ...EXPENSE_STEPS,
            "Exit Assumptions"
          ]
        )
        if (!existingModel) {
          setGrowthRates(GrowthRatesBasic);
        }
      }
      
    } else {

      if(selectedModelTypeInfo?.show_rental_units) {
        
        if(!existingModel){
          setExpenses(ExpensesBasic);
          setOperatingExpenses(prev => prev.length > 0 ? prev : OperatingExpensesBasic);
        }
        setSteps(
          [
            "Property Address",
            "General Property Assumptions",
            "Residential Rental Units",
            "Market Rent Assumptions",
            // "Retail Income",
            "Amenity Income",
            "Operating Expenses",
            "Net Operating Income",
            "Acquisition Financing",
            "Leasing Assumptions",
            "Refinancing",
            ...EXPENSE_STEPS,
            "Exit Assumptions"
          ]
        )
        if (!existingModel) {
          setGrowthRates(GrowthRatesBasic);
        }
      }
      else {
        if(!existingModel){
          setExpenses(ExpensesIndustrial);
        }
        setSteps(
          [
            "Property Address",
            "General Property Assumptions",
            "Retail Income",
            "Amenity Income",
            "Net Operating Income",
            "Acquisition Financing",
            "Refinancing",
            ...EXPENSE_STEPS,
            "Exit Assumptions"
          ]
        )
        if (!existingModel) {
          setGrowthRates(GrowthRatesBasic);
        }
      }
    }
  }, [selectedModelTypeInfo]);





  const EXPENSE_STEPS = [
    "Closing Costs",
    "Legal and Pre-Development Costs",
    "Reserves",
    "Hard Costs"
  ]


  const PRIMARY_STEPS = [
    "Property Address",
    "General Property Assumptions",
    "Residential Rental Units",
    "Market Rent Assumptions",
    "Retail Income",
    "Amenity Income",
    "Operating Expenses",
    "Base Income",
    "Recoverable Operating Expenses",
    "Recovery and Gross Potential Income",
    "TILC Reserves",
    "Income Summary",
  ]

  const SECONDARY_STEPS = [
    "Net Operating Income",
    "Acquisition Financing",
    "Leasing Assumptions",
    "Refinancing",
    ...EXPENSE_STEPS,
    "Exit Assumptions"
  ]



  useEffect(() => {
    // Create a unique key for this fetch operation
    const fetchKey = `${existingModel}-${modelId}-${user?.id}`;

    if (!existingModel || !modelId || !user?.id) return;

    // Skip if this exact fetch was already started (e.g. React Strict Mode double-invokes the effect)
    if (fetchModelDetailsRef.current === fetchKey) return;
    fetchModelDetailsRef.current = fetchKey;

    (window as any).__modelDetailsFetchFlag.add(fetchKey);
    clearGoogleSheetUrl();
    setModelCreationStarted(true);

    const fetchModelDetails = async () => {
      try {
        const token = await getAccessTokenSilently();
        const response = await fetch(BACKEND_URL + `/api/user_models_version/${modelId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          credentials: 'include',
        });
        const data = await response.json();
        if (data.error) {
          console.error("ERROR", data.error)
          // Surface it instead of silently leaving a blank, unsaveable form:
          // modelDetails stays at its empty initial value, so every field renders
          // its placeholder and Save & Exit stays disabled (google_sheet_url is
          // never set) with no indication why. Clearing the ref allows a retry.
          fetchModelDetailsRef.current = null;
          setLoadError(typeof data.error === 'string' ? data.error : 'Could not load this model.');
          return;
        }
        setLoadError('');
        data.google_sheet_url = '';

        generateGoogleSheet(data.model_type.id);
        setModelDetails(data);
        setMarketRentAssumptions(data.market_rent_assumptions);
        setSelectedModelType(data.model_type.id);
        setUnits(data.units);
        setMarketRentAssumptions(data.market_rent_assumptions);
        setGrowthRates(data.growth_rates);
        setAmenityIncome(data.amenity_income);
        setRetailIncome(data.retail_income);
        setOperatingExpenses(data.operating_expenses);
        setExpenses(data.expenses);
        setDevelopmentUnits(
          (data.development_units || []).map((d: any) => ({
            id: d.id,
            unit_type: d.unit_type,
            avg_sf: d.avg_sf,
            units: d.units,
            avg_rent: d.avg_rent // map backend key to FE key
          }))
        );
      } catch (err) {
        console.error('Error fetching model details:', err);
        fetchModelDetailsRef.current = null;
        setLoadError('Could not load this model. Please try again.');
      }
    };
    fetchModelDetails();

    return () => {
      (window as any).__modelDetailsFetchFlag.delete(fetchKey);
    };
  }, [existingModel, modelId, user?.id]);



  const handleNext = async () => {
    handleStepChange(activeStep, activeStep + 1)
  };

  const handleStepChange = (currentStep: number, nextStep: number) => {


    let current = steps[currentStep]
    let next = steps[nextStep]



    // Initial to secondary -> handle create int
    // Secondary to initials -> new sheet
    // Secondary to secondary -> nothing UNLESS expense, then do expense update

    let EXPANDED_PRIMARY_STEPS = [...PRIMARY_STEPS, "Rent Assumptions"];
    let EXPANDED_SECONDARY_STEPS = [...SECONDARY_STEPS, "Senior Construction Loan Financing", "Second Lien"];
    let EXPANDED_EXPENSE_STEPS = [...EXPENSE_STEPS, "Soft Costs"];

    if(selectedModelTypeInfo?.development_model) {
      EXPANDED_PRIMARY_STEPS = [...EXPANDED_PRIMARY_STEPS, "Hard Costs"];
      EXPANDED_SECONDARY_STEPS = [...EXPANDED_SECONDARY_STEPS].filter((step: string) => step !== "Hard Costs");
      EXPANDED_EXPENSE_STEPS = [...EXPANDED_EXPENSE_STEPS].filter((step: string) => step !== "Hard Costs");
    }

    if (EXPANDED_PRIMARY_STEPS.includes(current) && EXPANDED_SECONDARY_STEPS.includes(next)) {
      handleCreateIntermediate();
    }
    else if (EXPANDED_SECONDARY_STEPS.includes(current) && EXPANDED_PRIMARY_STEPS.includes(next)) {
      clearGoogleSheetUrl();
      setLeveredIrr('');
      setLeveredMoic('');
      setVariables({});
      setNoiTableValues([]);
      generateGoogleSheet(selectedModelType);
    }

    else if (EXPANDED_EXPENSE_STEPS.includes(current)) {

      handleUpdateExpenseTable(current);
    }

    // Mark next step as completed only if not already completed
    setCompletedSteps(prev => {
      const stepNumber = nextStep + 1;
      if (!prev.includes(stepNumber)) {
        return [...prev, stepNumber];
      }
      return prev;
    });

    setActiveStep(nextStep);
  };

  const handleBack = () => {
    if (activeStep === steps.indexOf("Operating Expenses") + 1) {
      clearGoogleSheetUrl();
      setLeveredIrr('');
      setLeveredMoic('');
      setVariables({});
      setNoiTableValues([]);
      generateGoogleSheet(selectedModelType);
    }
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  useEffect(() => {
    if (existingModel && steps.length > 0) {
      setCompletedSteps(Array.from({ length: steps.length }, (_, i) => i + 1));
    }
  }, [existingModel, steps.length]);



  const handleModelTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedModelType(event.target.value);
  };

  // Fix type error by specifying the type of updatesPending as an array of any (or a more specific type if known)
  const [updatesPending, setUpdatesPending] = useState<any[]>([]);
  const updatesPendingRef = useRef<any[]>([]);
  // Keep ref in sync — useEffect for state-driven updates, plus immediate
  // sync inside enqueueUpdate so the ref is never stale when a timeout fires.
  useEffect(() => { updatesPendingRef.current = updatesPending; }, [updatesPending]);

  const enqueueUpdate = (update: any) => {
    setUpdatesPending((prev) => {
      // Only match on field_id when it's a real (non-empty) value to avoid
      // collisions between different fields that both have field_id === "".
      const idx = prev.findIndex((u: any) =>
        (update.field_id && u.field_id === update.field_id) ||
        u.field_key === update.field_key
      );
      let next;
      if (idx >= 0) {
        next = prev.slice();
        next[idx] = { ...prev[idx], ...update };
      } else {
        next = [...prev, update];
      }
      // Sync the ref immediately so any pending setTimeout callback reads
      // the latest queue without waiting for the useEffect after render.
      updatesPendingRef.current = next;
      return next;
    });
  };


  const handleUpdateExpenseTable = async (sheet_name: string) => {

    const token = await getAccessTokenSilently();
    const sheetUrl = resolveGoogleSheetUrl();
    if (!sheetUrl) {
      console.error('Missing Google Sheet URL for expense update.');
      return;
    }
    const response = await fetch(BACKEND_URL + '/api/user_models_expense', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ sheet_name: sheet_name, expenses: expenses.filter((expense: any) => expense.type === sheet_name), google_sheet_url: sheetUrl, development_model: selectedModelTypeInfo?.development_model === true })
    })
  }

  const handleSingleFieldUpdate = async (updateObject: any) => {
    // Always enqueue the latest change so many rapid changes are coalesced
    enqueueUpdate(updateObject)
    // Clear any existing timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // Set a new timeout to delay the API call
    debounceTimeoutRef.current = setTimeout(async () => {
      // Wait up to 20s for mappings and sheet URL to be ready and not creating
      const isReady = () => (
        Object.keys(modelMappingRef.current || {}).length > 0 &&
        !!googleUrlRef.current &&
        Object.keys(variableMappingRef.current || {}).length > 0
      );
      const readyDeadline = Date.now() + 20_000;
      while ((!isReady() || isCreatingRef.current) && Date.now() < readyDeadline) {
        await new Promise((r) => setTimeout(r, 250));
      }
      if (!isReady() || isCreatingRef.current) {
        console.warn('[JIRA-350] debounce: not ready after 20s, skipping');
        return;
      }

      const toSend = updatesPendingRef.current;
      if (!toSend || toSend.length === 0) {
        console.warn('[JIRA-350] debounce: updatesPendingRef is empty, skipping');
        return;
      }

      console.log('[JIRA-350] debounce fired, sending updates:', toSend.map((u: any) => u.field_key));
      setFinalMetricsCalculating(true);
      const token = await getAccessTokenSilently();
      const payload = { updates: toSend, variable_mapping: variableMappingRef.current, model_mapping: modelMappingRef.current, google_sheet_url: googleUrlRef.current, development_model: selectedModelTypeInfo?.development_model === true };

      const doRequest = async (retry = false): Promise<{ ok: boolean; result?: any }> => {
        const res = await fetch(BACKEND_URL + '/api/user_models_single_field_updates', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify(payload)
        });
        const data = await res.json();
        if (res.ok && data.result) return { ok: true, result: data.result };
        if (!res.ok && retry) return { ok: false };
        if (!res.ok && !retry) {
          await new Promise((r) => setTimeout(r, 2000));
          return doRequest(true);
        }
        return { ok: false };
      };

      try {
        const { ok, result: resResult } = await doRequest();
        if (ok && resResult) {
          console.log('[JIRA-350] API success. Sr. Cons vars:', {
            'LTC': resResult.variables?.['Sr. Cons: LTC'],
            'Project Costs Funded': resResult.variables?.['Sr. Cons: Project Costs Funded'],
            'Interest Reserve': resResult.variables?.['Sr. Cons. Estimated Interest Reserve'],
          });
          setLeveredIrr(resResult.levered_irr);
          setLeveredMoic(resResult.levered_moic);
          setVariables(resResult.variables);
          setNoiTableValues(resResult.NOI || []);
          setUpdatesPending((prev) => {
            const sentKeys = new Set(toSend.map((u: any) => u.field_id || u.field_key));
            return prev.filter((u: any) => !sentKeys.has(u.field_id || u.field_key));
          });
        } else {
          console.warn('[JIRA-350] API returned ok=false or empty result');
        }
      } catch (err) {
        console.error('[JIRA-350] API call error:', err);
      } finally {
        setFinalMetricsCalculating(false);
      }
    }, 500); // 500ms delay
  }

  const handleFieldChange = (
    fieldId: string,
    field_key: string,
    value: string | number,
    isTimePhased: boolean = false,
    timeField?: 'start_month' | 'end_month',
    fieldType?: string // <-- optionally pass fieldType if available
  ) => {
  // If fieldId is empty, resolve it from the model type section definitions.
  // This handles cases where the field isn't yet in modelDetails.user_model_field_values
  // (e.g., due to stale closure in non-functional setModelDetails calls).
  if (!fieldId && field_key && selectedModelTypeInfo?.sections) {
    for (const section of selectedModelTypeInfo.sections) {
      const match = section.fields.find((f: any) => f.field_key === field_key);
      if (match) {
        fieldId = match.id;
        break;
      }
    }
  }

  // Preserve leading zero for decimals lik
  // e 0.25; only strip for integers like 01
  if (typeof value === 'string' && value.length > 1 && value.startsWith('0') && value[1] !== '.') {
    value = value.slice(1);
  }

    // Helper to convert YYYY-MM-DD to MM/DD/YYYY
    const formatDate = (dateStr: string) => {
      if (!dateStr || typeof dateStr !== 'string') return dateStr;
      // Only format if matches YYYY-MM-DD
      const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (match) {
        const [, year, month, day] = match;
        return `${month}/${day}/${year}`;
      }

      return dateStr;
    };

    // Compute the pending update object outside the state updater so that
    // the side-effectful handleSingleFieldUpdate call is never made from
    // inside a React state updater (which must be pure).
    let pendingUpdate: any = null;

    setModelDetails(prevDetails => {
      // Try to get the field_type from selectedModelTypeInfo if not passed
      let actualFieldType = fieldType;


      let processedValue = value;
      if (actualFieldType === 'date' && typeof value === 'string') {
        processedValue = formatDate(value);
      }

      const existingFieldIndex = prevDetails.user_model_field_values.findIndex(
        (fieldValue: any) =>
          (fieldId && fieldValue.field_id === fieldId) ||
          (!fieldId && fieldValue.field_key === field_key)
      );
      let updatedFieldValues;
      if (existingFieldIndex !== -1) {
        updatedFieldValues = [...prevDetails.user_model_field_values];
        if (timeField) {
          updatedFieldValues[existingFieldIndex] = {
            ...updatedFieldValues[existingFieldIndex],
            [timeField]: processedValue
          };
        } else {
          updatedFieldValues[existingFieldIndex] = {
            ...updatedFieldValues[existingFieldIndex],
            value: processedValue
          };
        }
      } else {
        const newFieldValue = timeField
          ? { field_id: fieldId, field_key: field_key, [timeField]: processedValue }
          : { field_id: fieldId, field_key: field_key, value: processedValue };
        updatedFieldValues = [...prevDetails.user_model_field_values, newFieldValue];
      }


      if (selectedModelTypeInfo && selectedModelTypeInfo.sections) {

        const section = selectedModelTypeInfo.sections.find((section: any) =>
          section.fields.some((f: any) => f.id === fieldId || f.field_key === field_key)
        );

        if (section) {

          let sectionFieldType = section.fields.find(
            (f: any) => f.id === fieldId || f.field_key === field_key
          )?.field_type;
          let updateObject = { ...updatedFieldValues.find((field: any) => field.field_key === field_key) };

          updateObject["field_type"] = sectionFieldType;
          updateObject["section"] = section.name;

          // Only trigger a backend update if this is a real field change
          const existingField = prevDetails.user_model_field_values.find(
            (fv: any) =>
              (fieldId && fv.field_id === fieldId) ||
              (!fieldId && fv.field_key === field_key)
          );
          const valueChanged = !existingField || existingField.value !== processedValue;


          if (section.name !== "General Property Assumptions" && section.name !== "Retail Leasing Assumptions" && valueChanged) {
            pendingUpdate = updateObject;
          }

        }

      }
      return { ...prevDetails, user_model_field_values: updatedFieldValues };
    });

    // Trigger the debounced API update outside the state updater
    if (pendingUpdate) {
      console.log('[JIRA-350] handleFieldChange: triggering debounced update for', pendingUpdate.field_key, '=', pendingUpdate.value);
      handleSingleFieldUpdate(pendingUpdate);
    }
  };

  useEffect(() => {
    const fetchModelTypes = async () => {
      try {
        const token = await getAccessTokenSilently({
          authorizationParams: {
            audience: process.env.REACT_APP_AUTH0_AUDIENCE,
            scope: "openid profile email"
          }
        });
        const res = await fetch(BACKEND_URL + '/api/model_types', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          credentials: 'include',
        });
        const data = await res.json();

        setModelTypes(data);
      } catch (err) {
        console.error('Error fetching model types:', err);
      }
    };

    fetchModelTypes();
  }, [getAccessTokenSilently]);

  useEffect(() => {
    if (selectedModelType) {
      const fetchModelType = async () => {
        try {
          const token = await getAccessTokenSilently();
          const res = await fetch(BACKEND_URL + `/api/model_types/${selectedModelType}`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            credentials: 'include',
          });
          const data = await res.json();

          if (data.development_model) {
            data.sections.forEach((section: any) => {
              if (section.name === "General Property Assumptions") {
                section.fields.forEach((field: any) => {
                  if (field.field_key === "Asking Price") {
                    field.field_title = FIELD_TITLE_CONSTANTS.ASKING_PRICE_LAND;
                  } else if (field.field_key === "Acquisition Price") {
                    field.field_title = FIELD_TITLE_CONSTANTS.ACQUISITION_VALUE_LAND;
                  }
                });
              }
            });
          }

          setSelectedModelTypeInfo(data);
          interface FieldValue {
            field_id: string;
            field_key: string;
            field_title: string;
            field_type: string;
            value: string | number;
            start_month?: number;
            end_month?: number;
          }
          if (!existingModel) {
            let fieldValues: FieldValue[] = [];
            for (const section of data.sections) {
              // Seed only the fields the wizard renders. Seeding soft-deleted
              // fields used to paper over the completeness check disagreeing
              // with the render path (2026-08-20) and wrote value rows against
              // retired field ids.
              // <any>: data.sections is untyped, so T cannot be inferred and
              // falls back to the ModelTypeField constraint, whose id/field_key/
              // field_title/field_type are all optional and which has no
              // default_value at all -- TS2322 on field_id, then more. Pinning T
              // keeps the pre-PR#99 `any` behaviour of this loop.
              for (const field of visibleFields<any>(section.fields)) {
                if (field.time_phased) {
                  fieldValues.push({
                    field_id: field.id,
                    field_key: field.field_key,
                    field_title: field.field_title,
                    field_type: field.field_type,
                    value: field.default_value || '',
                    start_month: 0,
                    end_month: 0
                  });
                }
                else {
                  fieldValues.push({
                    field_id: field.id,
                    field_key: field.field_key,
                    field_title: field.field_title,
                    field_type: field.field_type,
                    value: field.default_value || ''
                  });
                }
              }
            }
            setModelDetails(prevDetails => ({ ...prevDetails, user_model_field_values: fieldValues }));
          }
        } catch (err) {
          console.error('Error fetching selected model type:', err);
        }
      };

      fetchModelType();
    }
  }, [selectedModelType, getAccessTokenSilently]);

  useEffect(() => {
    if (selectedModelTypeInfo) {
      // Sort sections and fields by their order
      selectedModelTypeInfo.sections.sort((a, b) => a.order - b.order);
      selectedModelTypeInfo.sections.forEach(section => {
        section.fields.sort((a, b) => a.order - b.order);
      });
    }
  }, [selectedModelTypeInfo]);

  useEffect(() => {
    if (amenityIncome.length === 0) {
      setAmenityIncome(AmenityIncomeBasic);
    }
  }, []);

  // Keep modelDetails.development_units in sync with table rows
  useEffect(() => {
    setModelDetails(prev => ({ ...prev, development_units: developmentUnits }));
  }, [developmentUnits]);

  // Field-level completeness lives in utils/sectionCompletion so it is unit
  // tested and cannot drift from the render path again. See that file's header
  // for the 2026-08-20 edit-model lockout this indirection prevents.

  /** The model type section rendered at a given step, if the step is field-driven. */
  const sectionForStep = (stepName: string) =>
    selectedModelTypeInfo?.sections?.find((section: any) => section.name === stepName);

  /**
   * Titles of the fields keeping the user on this step. Surfaced next to the
   * disabled Continue button: a gate the user cannot see is a gate they cannot
   * clear, which is what turned this defect into a day of lost production time.
   */
  const currentStepBlockers = (() => {
    const stepName = steps[activeStep];
    if (!stepName || stepName === 'Property Address') return [];
    const section = sectionForStep(stepName);
    if (!section) return [];
    return blockingFieldLabels(section.fields, modelDetails.user_model_field_values);
  })();

const isStepComplete = (step: number) => {
    // Global validation: any Retail expense with end_month < start_month blocks progression
    
    if (steps[activeStep] === "Property Address") {
      return modelDetails.street_address && modelDetails.city && modelDetails.state && modelDetails.zip_code;
    } else if (steps[activeStep] === "General Property Assumptions" && selectedModelTypeInfo) {
      const currentSection = sectionForStep('General Property Assumptions');
      if (!currentSection) return false; // Ensure currentSection is defined
      // Validates only the fields this step renders (active ones), resolved by
      // field_id, and treats an absent value row as unset rather than invalid.
      return isSectionComplete(currentSection.fields, modelDetails.user_model_field_values);
    }
    else if (steps[activeStep] === "Residential Rental Units") {
      // Check that there is at least one unit, the sum of all unit square_feet > 0,
      // and none of the units have current_rent === null
      return (
        units.length > 0 &&
        units.map(unit => unit.square_feet).reduce((sum, sf) => sum + sf, 0) > 0 &&
        units.every(unit => unit.current_rent !== null)
      );
    }
    else if (steps[activeStep] === "Rent Assumptions") {
      if (selectedModelTypeInfo?.development_model) {
        if (developmentUnits.length === 0) {
          return false;
        }
        return developmentUnits.every((unit) => unit.unit_type && unit.avg_sf && unit.units && unit.avg_rent);

      }
    }
    else if (activeStep === steps.indexOf("Market Rent Assumptions")) {
      return (
        units.length > 0 &&
        units.map(unit => unit.square_feet).reduce((sum, sf) => sum + sf, 0) > 0 &&
        units.every(unit => unit.current_rent !== null)
      );
      // return marketRentAssumptions.every((assumption) => assumption.layout && assumption.pf_rent);
    }
    else if (steps[activeStep] === "Senior Construction Loan Financing") {
      return true;
    }
    else if (steps[activeStep] === "Leasing Assumptions") {
      return true;
    }
    else if (steps[activeStep] === "Second Lien") {
      return true;
    }
    else if (steps[activeStep] === "Refinancing") {
      return true;
    }
    else if (activeStep === steps.indexOf("Operating Expenses")) {
      return true;
      // return operatingExpenses.every((expense) => expense.name && expense.start_month && expense.end_month && expense.amount);
    }
    else if (activeStep === steps.indexOf("Retail Income")) {
      return true;
      // return operatingExpenses.every((expense) => expense.name && expense.start_month && expense.end_month && expense.amount);
    }
    else if (activeStep === steps.indexOf("Base Income")) {
      return true;
    }
    else if (activeStep === steps.indexOf("Recoverable Operating Expenses")) {
      return true;
    }
    else if (activeStep === steps.indexOf("Recovery and Gross Potential Income")) {
      return true;
    }
    else if (activeStep === steps.indexOf("TILC Reserves")) {
      return true;
    }
    else if (activeStep === steps.indexOf("Income Summary")) {
      return true;
    }
    else if (activeStep === steps.indexOf("Soft Costs")) {
      return true;
    }
    else if (activeStep === steps.indexOf("Amenity Income")) {
      return amenityIncome.every((income) =>
        income.name &&
        income.utilization !== undefined &&
        income.utilization !== null &&
        income.unit_count !== undefined &&
        income.unit_count !== null &&
        income.monthly_fee !== undefined &&
        income.monthly_fee !== null
      );
    }
    else if (['Closing Costs', 'Legal and Pre-Development Costs', 'Reserves', 'Hard Costs'].includes(steps[activeStep])) {
      try {
        const retailInvalid = (expenses || [])
          .some((expense: any) => {
            const sm = Number(expense?.start_month);
            const em = Number(expense?.end_month);
            return Number.isFinite(sm) && Number.isFinite(em) && em < sm;
          });
        if (retailInvalid) {
          return false;
        }
      } catch {}
      return true;
    }
    else if (activeStep === steps.indexOf("Net Operating Income")) {
      return true;
    }
    else if (activeStep === steps.indexOf("Rental Unit Growth Rates")) {
      if (!modelDetails.google_sheet_url) {
        return false;
      }
      return growthRates.some((rate: any) => rate.type === 'rental');
    } else if (selectedModelTypeInfo) {
      const currentSection = sectionForStep(steps[activeStep]);
      if (!currentSection) return false; // Ensure currentSection is defined
      // Same rules as General Property Assumptions above — every field-driven
      // step goes through the one tested implementation.
      return isSectionComplete(currentSection.fields, modelDetails.user_model_field_values);
    }
    return true;
  };

  const prepareDataForPostRequest = () => {
    const userModelData = {
      name: modelDetails.name,
      street_address: modelDetails.street_address,
      city: modelDetails.city,
      state: modelDetails.state,
      zip_code: modelDetails.zip_code,
      latitude: modelDetails.latitude ?? null,
      longitude: modelDetails.longitude ?? null,
      model_type_id: selectedModelType,
      user_id: user?.id, // Use actual user ID from context
      google_sheet_url: modelDetails.google_sheet_url,
      id: existingModel ? modelDetails.id : null
    };


    const userModelFieldValuesData = modelDetails.user_model_field_values.map((fieldValue: any) => ({
      field_id: fieldValue.field_id,
      field_key: fieldValue.field_key,
      field_type: fieldValue.field_type,
      value: fieldValue.value,
      start_month: fieldValue.start_month || null,
      end_month: fieldValue.end_month || null,
    }));


    return {
      ...userModelData,
      user_model_field_values: userModelFieldValuesData,
      units,
      market_rent_assumptions: marketRentAssumptions,
      growth_rates: growthRates,
      amenity_income: amenityIncome,
      operating_expenses: operatingExpenses,
      retail_income: retailIncome,
      expenses: expenses,
      development_units: developmentUnits.map((d) => ({
        unit_type: d.unit_type,
        avg_sf: d.avg_sf,
        units: d.units,
        avg_rent: (d as any).avg_rent // map FE key to backend key
      })),
    };
  };


  const handleCreateIntermediate = async () => {
    setIsCreating(true);
    let waited = 0;
    const maxWait = 20000; // 20 seconds
    const interval = 500; // 0.5 seconds
    setFinalMetricsCalculating2(true);
    setFinalMetricsCalculating(true);

    // Helper to wait for google_sheet_url to exist
    const waitForGoogleSheetUrl = () => {
      return new Promise<void>((resolve, reject) => {
        const check = () => {
          const sheetUrl = resolveGoogleSheetUrl();
          if (sheetUrl) {
            resolve();
          } else if (waited >= maxWait) {
            reject(new Error("Timeout: google_sheet_url not set after 20 seconds"));
          } else {
            waited += interval;
            setTimeout(check, interval);
          }
        };
        check();
      });
    };

    try {
      await waitForGoogleSheetUrl();
      const token = await getAccessTokenSilently();
      const data = prepareDataForPostRequest();
        data.google_sheet_url = resolveGoogleSheetUrl();
        // Pass development_model to backend for intermediate processing
        (data as any).development_model = selectedModelTypeInfo?.development_model === true;


      const response = await fetch(BACKEND_URL + '/api/user_models_intermediate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(data)
      });
      const result = await response.json();

      if (response.ok) {
        setModelMapping(result.result?.model_mapping || {});
        setVariableMapping(result.result?.variable_mapping || {});
        setLeveredIrr(result.result?.levered_irr || 'N/A');
        setLeveredMoic(result.result?.levered_moic || 'N/A');
        setVariables(result.result?.variables || {});
        setNoiTableValues(result.result?.NOI || []);
        setIsCreating(false);
        setFinalMetricsCalculating(false);
        setFinalMetricsCalculating2(false);
      } else {
        console.error('Error creating user model:', result.error);
      }
    } catch (err) {
      console.error('Error during model creation:', err);
      setFinalMetricsCalculating(false);
      setFinalMetricsCalculating2(false);
    } finally {
      setIsCreating(false);
      setFinalMetricsCalculating(false);
      setFinalMetricsCalculating2(false);
    }
  }



  const handleSaveAndExit = async () => {
    let waited = 0;
    const interval = 500; // 0.5 seconds
    const maxWait = 30000; // 20 seconds

    // Helper to wait for google_sheet_url to exist AND isCreating to be false
    const waitForGoogleSheetUrlAndNotCreating = () => {
      return new Promise<void>((resolve, reject) => {
        const check = () => {
          const sheetUrl = resolveGoogleSheetUrl();
          if (sheetUrl && !isCreatingRef.current) {
            resolve();
          } else if (waited >= maxWait) {
            reject(new Error("Timeout: google_sheet_url not set or isCreating still true after 20 seconds"));
          } else {
            waited += interval;
            setTimeout(check, interval);
          }
        };
        check();
      });
    };
    try {

      let EXPANDED_PRIMARY_STEPS = [...PRIMARY_STEPS, "Rent Assumptions"];
      let EXPANDED_SECONDARY_STEPS = [...SECONDARY_STEPS, "Senior Construction Loan Financing", "Second Lien"];
      let EXPANDED_EXPENSE_STEPS = [...EXPENSE_STEPS, "Soft Costs"];

      if(selectedModelTypeInfo?.development_model) {
        EXPANDED_PRIMARY_STEPS = [...EXPANDED_PRIMARY_STEPS, "Hard Costs"];
        EXPANDED_SECONDARY_STEPS = [...EXPANDED_SECONDARY_STEPS].filter((step: string) => step !== "Hard Costs");
        EXPANDED_EXPENSE_STEPS = [...EXPANDED_EXPENSE_STEPS].filter((step: string) => step !== "Hard Costs");
      }
      let current = steps[activeStep];
      if (EXPANDED_PRIMARY_STEPS.includes(current)) {

        await handleCreateIntermediate();
      }
      else if (EXPANDED_EXPENSE_STEPS.includes(current)) {
        await handleUpdateExpenseTable(current);
      }
      await waitForGoogleSheetUrlAndNotCreating();
      setIsCreating(true);
      const token = await getAccessTokenSilently();
      const data = prepareDataForPostRequest();
      data.google_sheet_url = resolveGoogleSheetUrl();
      (data as any).development_model = selectedModelTypeInfo?.development_model === true;
      const response = await fetch(BACKEND_URL + '/api/user_models_new_version', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(data)
      });
      const result = await response.json();
      if (response.ok) {
        setHideMainSidebar(false);
        setModelCreationStarted(false);
        setSelectedModelTypeId('');
        navigate(`/models/${result.id}`);
      } else {
        console.error('Error creating user model:', result.error);
        setLoadError(typeof result.error === 'string' ? result.error : 'Could not save this model.');
      }
    } catch (err) {
      console.error('Error during model creation:', err);
      setLoadError('Could not save this model. Please try again.');
    } finally {
      setIsCreating(false);
    }
  }



  const handleFinish = async () => {
    let waited = 0;
    const interval = 500; // 0.5 seconds
    const maxWait = 20000; // 20 seconds

    // Helper to wait for google_sheet_url to exist AND isCreating to be false
    const waitForGoogleSheetUrlAndNotCreating = () => {
      return new Promise<void>((resolve, reject) => {
        const check = () => {
          const sheetUrl = resolveGoogleSheetUrl();
          if (sheetUrl && !isCreatingRef.current) {
            resolve();
          } else if (waited >= maxWait) {
            reject(new Error("Timeout: google_sheet_url not set or isCreating still true after 20 seconds"));
          } else {
            waited += interval;
            setTimeout(check, interval);
          }
        };
        check();
      });
    };

    try {

      await waitForGoogleSheetUrlAndNotCreating();
      setIsCreating(true);
      const token = await getAccessTokenSilently();
      const data = prepareDataForPostRequest();
      data.google_sheet_url = resolveGoogleSheetUrl();
      (data as any).development_model = selectedModelTypeInfo?.development_model === true;
      const response = await fetch(BACKEND_URL + '/api/user_models', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(data)
      });
      const result = await response.json();
      if (response.ok) {
        setHideMainSidebar(false);
        setModelCreationStarted(false);
        setSelectedModelTypeId('');
        navigate(`/models/${result.id}`);
      } else {
        console.error('Error creating user model:', result.error);
        setLoadError(typeof result.error === 'string' ? result.error : 'Could not save this model.');
      }
    } catch (err) {
      console.error('Error during model creation:', err);
      setLoadError('Could not save this model. Please try again.');
    } finally {
      setIsCreating(false);
    }
  };

  // Function to check for duplicate names
  const checkForDuplicateNames = () => {
    const names = growthRates.map((rate: any) => rate.name);
    const uniqueNames = new Set(names);
    return uniqueNames.size !== names.length;
  };

  // Function to check if at least one rental unit growth rate is added
  const checkForRentalUnitGrowthRate = () => {

    return growthRates.some((rate: any) => rate.type === 'rental');
  };

  // Function to check for duplicate layout names
  const checkForDuplicateLayouts = () => {
    const layouts = marketRentAssumptions.map((assumption) => assumption.layout);
    const uniqueLayouts = new Set(layouts);
    return uniqueLayouts.size !== layouts.length;
  };


  useEffect(() => {
    setNameError(checkForDuplicateLayouts());
  }, [marketRentAssumptions]);


  // Imported units place their vacate month relative to the Model Start Date,
  // so moving that date has to move them with it. Only rent-roll units with a
  // lease end are touched; reDeriveVacateMonths returns the same array when
  // nothing moves, so this cannot loop.
  const modelStartDateValue = modelDetails?.user_model_field_values?.find(
    (f: any) => f?.field_key === 'Model Start Date'
  )?.value;

  useEffect(() => {
    if (!modelStartDateValue) return;
    setUnits((prev) => reDeriveVacateMonths(prev, modelStartDateValue));
  }, [modelStartDateValue]);


  const generateGoogleSheet = async (selectedModelType: string) => {
    setModelMapping({});
    setVariableMapping({});
    try {
      const token = await getAccessTokenSilently();
      const userId = user?.id;

      const response = await fetch(BACKEND_URL + `/api/model_types/${selectedModelType}/generate_sheet`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          user_id: userId
        })
      });

      const data = await response.json();
      if (response.ok) {
        googleUrlRef.current = data.sheet_url;
        setModelDetails(prevDetails => ({
          ...prevDetails,
          google_sheet_url: data.sheet_url
        }));
        if (process.env.REACT_APP_DEVELOPMENT_MODE === "local" || process.env.REACT_APP_DEVELOPMENT_MODE === "dev") {
          console.log("google sheet url", data.sheet_url);
        }
        (window as any).__generatedSheetUrl = data.sheet_url;
      } else {
        console.error('Error generating Google Sheet:', data.error);
        // Without a workbook the stepper waits forever on "Preparing the
        // model workbook"; say what the API said instead.
        setLoadError(typeof data.error === 'string' ? data.error : 'Could not prepare the model workbook.');
      }
    } catch (err) {
      console.error('Error during Google Sheet generation:', err);
      setLoadError('Could not prepare the model workbook. Please try again.');
    }
  }

  const getStarted = async () => {
    setModelCreationStarted(true);
    // setIntroStepComplete(true);
    if (selectedModelType && !existingModel) {
      generateGoogleSheet(selectedModelType);
    }
  };


  
  const rentalNames = growthRates
    .filter(rate => rate.type === 'rental')
    .map(rate => rate.name.trim().toLowerCase());

  const isDuplicate = rentalNames.includes(newRentalGrowthName.trim().toLowerCase());
  const isAddDisabled = !newRentalGrowthName.trim() || isDuplicate;

  // When mappings/sheet become ready and there are queued updates, flush them
  // through the existing handleSingleFieldUpdate path (which properly updates metrics).
  useEffect(() => {
    const ready =
      Object.keys(modelMappingRef.current || {}).length > 0 &&
      !!googleUrlRef.current &&
      Object.keys(variableMappingRef.current || {}).length > 0;
    if (ready && !isCreatingRef.current && updatesPendingRef.current.length) {
      if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);
      // Re-trigger the existing debounced flush with a no-op enqueue
      handleSingleFieldUpdate(updatesPendingRef.current[0]);
    }
  }, [modelMapping, variableMapping, modelDetails.google_sheet_url, isCreating]);






  if (userLoaded && isViewer && !existingModel) {
    return <Navigate to="/" replace />;
  }

  return (
    <>
      {isViewer && existingModel && (
        <Container maxWidth="lg" sx={{ mt: 2 }}>
          <Alert severity="info">
            View only. As a viewer you can look through every step of this model, but not change it.
          </Alert>
        </Container>
      )}
      {loadError && (
        <Container maxWidth="lg" sx={{ mt: 2 }}>
          <Alert severity="error" onClose={() => setLoadError('')}>
            {loadError}
          </Alert>
        </Container>
      )}
      {!introStepComplete && !existingModel && (
        <Container maxWidth="lg" sx={{ mt: 6, maxWidth: '1000px', mx: 'auto' }}>
          <ModelIntroSteps
            handleFieldChange={handleFieldChange}
            modelDetails={modelDetails}
            modelTypes={modelTypes}
            selectedModelType={selectedModelType}
            handleModelTypeChange={handleModelTypeChange}
            getStarted={getStarted}
          />
        </Container>
      )}
      {introStepComplete && (
        <ModelStepper
          steps={steps}
          activeStep={activeStep}
          completedSteps={completedSteps}
          onStepChange={handleStepChange}
          onNext={
            activeStep === steps.length - 1
              ? isViewer
                ? exitReadOnly
                : existingModel
                ? handleSaveAndExit
                : handleFinish
              : handleNext
          }
          onBack={handleBack}
          isStepComplete={isStepComplete}
          stepBlockers={currentStepBlockers}
          isCreating={isCreating}
          leveredIrr={leveredIrr}
          leveredMoic={leveredMoic}
          finalMetricsCalculating={finalMetricsCalculating}
          finalMetricsCalculating2={finalMetricsCalculating2}
          showRetail={selectedModelTypeInfo?.show_retail}
          existingModel={existingModel}
          handleSaveAndExit={isViewer ? exitReadOnly : handleSaveAndExit}
          modelDetails={modelDetails}
          unitSqFtTotal={units.reduce((acc, u) => acc + (u.square_feet || 0), 0)}
          retailSqFtTotal={retailIncome.reduce((acc, r) => acc + (r.square_feet || 0), 0)}
        >
          {/* A disabled fieldset disables every native input, button and
              radio inside it; MUI selects and autocompletes are divs, so
              pointer events are switched off for those. Viewers only. */}
          <Box
            component="fieldset"
            disabled={isViewer}
            sx={{
              border: 0, m: 0, p: 0, minWidth: 0,
              ...(isViewer ? {
                '& .MuiSelect-select, & .MuiAutocomplete-root, & [contenteditable="true"]': { pointerEvents: 'none' },
              } : {}),
            }}
          >
          {steps[activeStep] === "Property Address" && (
            <>
              <Box sx={{ maxWidth: "800px", mx: "auto", px: { xs: 2, sm: 4 }, pb: 4 }}>
                <ContentCard>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <StandardInput
                    label="Property Name"
                    placeholder="e.g., Downtown Apartment Building"
                    value={modelDetails.name}
                    onChange={(e) => { const v = e.target.value; setModelDetails(prev => ({ ...prev, name: v })); }}
                    fullWidth
                  />

                  <AddressAutocomplete
                    value={modelDetails.street_address}
                    onChange={(value) => setModelDetails(prev => ({ ...prev, street_address: value, latitude: null, longitude: null }))}
                    onAddressSelect={(address) => setModelDetails(prev => ({
                      ...prev,
                      street_address: address.street_address,
                      city: address.city,
                      state: address.state,
                      zip_code: address.zip_code,
                      latitude: address.latitude ?? null,
                      longitude: address.longitude ?? null,
                    }))}
                    fullWidth
                    required
                  />

                  <FormRow>
                    <StandardInput
                      label="City"
                      placeholder="City"
                      value={modelDetails.city}
                      onChange={(e) => { const v = e.target.value; setModelDetails(prev => ({ ...prev, city: v })); }}
                      required
                    />
                    <StandardInput
                      label="State"
                      placeholder="State"
                      value={modelDetails.state}
                      onChange={(e) => { const v = e.target.value; setModelDetails(prev => ({ ...prev, state: v })); }}
                      required
                    />
                    <StandardInput
                      label="Zip Code"
                      placeholder="Zip Code"
                      value={modelDetails.zip_code}
                      onChange={(e) => { const v = e.target.value; setModelDetails(prev => ({ ...prev, zip_code: v })); }}
                      required
                    />
                  </FormRow>
                </Box>
              </ContentCard>
              </Box>
            </>
          )}
          {steps[activeStep] === "Rental Unit Growth Rates" && (
            <Box sx={{ width: "100%", mx: "auto" }}>
              {/* <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }} >Rental Unit Growth Rates</Typography>
              <Box sx={{ mt: 0, mb: 2 }}>
                {growthRates
                  .filter((rate: any) => rate.type === 'rental')
                  .map((rate: any, idx: number) => {
                    const rentalIndexes = growthRates
                      .map((r: any, i: number) => r.type === 'rental' ? i : -1)
                      .filter((i: number) => i !== -1);
                    return (
                      <Box
                        key={idx}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          mb: 1.5,
                          px: 2,
                          py: 1,
                          borderRadius: 2,
                          bgcolor: '#f8f9fa',
                          // boxShadow: 1,
                          border: '1px solid #e0e0e0',
                          transition: 'background 0.2s',
                          '&:hover': { bgcolor: '#f1f3f6' },
                        }}
                      >
                        <Box
                          sx={{
                            width: "calc(100% - 260px)",
                            pr: 2,
                            display: 'flex',
                            alignItems: 'center',
                            borderRight: '1px solid #e0e0e0',
                            height: '100%',
                          }}
                        >
                          <Typography
                            variant="body1"
                            sx={{
                              fontWeight: 600,
                            }}
                          >
                            {rate.name}
                          </Typography>
                        </Box>
                        <Box sx={{ flex: 1, pl: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                          <TextField
                            label="Growth Rate (%)"
                            type="number"
                            value={rate.value}
                            onChange={e => {
                              const newGrowthRates = growthRates.map((r: any, i: number) =>
                                i === rentalIndexes[idx] ? { ...r, value: Number(e.target.value) } : r
                              );
                              setGrowthRates(newGrowthRates);
                            }}
                            size="small"
                            placeholder="Enter percentage"
                            inputProps={{ min: 0 }}
                            sx={{
                              width: 120,
                              bgcolor: '#fff',
                              borderRadius: 1,
                              '& .MuiInputBase-input': { textAlign: 'right', fontWeight: 500 },
                            }}
                          />
                          {growthRates.filter((r: any) => r.type === 'rental').length > 1 && (() => {
                            const removeIdx = rentalIndexes[idx];
                            const growthRateName = rate.name;
                            const isUsedByUnit = units.some((unit: any) => unit.rent_type === growthRateName);
                            const tooltipText = isUsedByUnit
                              ? "Cannot delete: This growth rate is used by a unit's rent type."
                              : "";

                            return (
                              <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                                <Tooltip
                                  title={tooltipText}
                                  disableHoverListener={!isUsedByUnit}
                                  disableFocusListener={!isUsedByUnit}
                                  disableTouchListener={!isUsedByUnit}
                                  arrow
                                >
                                  <span>
                                    <Button
                                      color="error"
                                      onClick={() => {
                                        const newGrowthRates = growthRates.filter((_: any, i: number) => i !== removeIdx);
                                        setGrowthRates(newGrowthRates);
                                      }}
                                      size="small"
                                      sx={{
                                        minWidth: 0,
                                        px: 1.5,
                                        fontWeight: 600,
                                        borderRadius: 1,
                                        bgcolor: isUsedByUnit ? '#f5f5f5' : '#fff',
                                        color: isUsedByUnit ? 'text.disabled' : 'error.main',
                                        boxShadow: 0,
                                        '&:hover': {
                                          bgcolor: isUsedByUnit ? '#f5f5f5' : '#ffeaea',
                                        },
                                      }}
                                      disabled={isUsedByUnit}
                                    >
                                      Delete
                                    </Button>
                                  </span>
                                </Tooltip>
                              </span>
                            );
                          })()}
                        </Box>
                      </Box>
                    );
                  })}
              </Box> */}
              {/* <Box sx={{ display: 'flex', alignItems: 'center', mt: 0, width: '100%' }}>
                <TextField
                  label="New Rental Growth Name"
                  value={newRentalGrowthName}
                  onChange={(e) => setNewRentalGrowthName(e.target.value)}
                  size="small"
                  sx={{ mr: 2, flex: 1 }}
                  error={isDuplicate}
                  helperText={isDuplicate ? "Name already exists" : ""}
                  fullWidth
                />
                <Button
                  variant="outlined"
                  onClick={() => {
                    setGrowthRates([
                      ...growthRates,
                      { name: newRentalGrowthName.trim(), value: 0, type: 'rental' }
                    ]);
                    setNewRentalGrowthName('');
                  }}
                  disabled={isAddDisabled}
                  sx={{ mt: 0, whiteSpace: 'nowrap' }}
                >
                  Add Rental Unit Growth Rate
                </Button>
              </Box> */}

              <UnitTable
                highlightedFields={['rent_type', 'vacate_flag', 'vacate_month']}
                units={units}
                setUnits={setUnits}
                growthRates={growthRates}
                marketRentAssumptions={marketRentAssumptions}
                showMarketRentLayouts={false}
                showMarketRentAssumptions={true}
                setMarketRentAssumptions={setMarketRentAssumptions}
                showGrowthRates={true}
                setGrowthRates={setGrowthRates}
                modelDetails={modelDetails}
                growthRatesOnly={true}
                vacate={true}
                activeStepTitle={steps[activeStep]}
              />


            </Box>

          )}
          {steps[activeStep] === "Market Rent Assumptions" && (
            <Box sx={{ maxWidth: "1400px", width: "100%", mx: "auto" }}>
              {/* <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>Market Rent Assumptions</Typography> */}
              {/* <Box sx={{ mt: 0 }}>
                {marketRentAssumptions
                  .filter(assumption =>
                    units.some(unit => unit.layout === assumption.layout)
                  )
                  .map((assumption, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: 1.5,
                        alignItems: 'center',
                        mb: 1.2,
                        px: 1.5,
                        py: 1,
                        backgroundColor: '#f8f9fa',
                        borderRadius: 1,
                        border: '1px solid #e0e0e0',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            fontSize: '1.08rem',
                            color: '#222',
                            mr: 1,
                          }}
                        >
                          Layout:
                        </Typography>
                        <Typography
                          sx={{
                            fontWeight: 500,
                            fontSize: '1.08rem',
                            color: '#444',
                          }}
                        >
                          {assumption.layout}
                        </Typography>
                      </Box>
                      <NumberInput
                        label="Pro Forma $ Rent"
                        value={assumption.pf_rent}
                        onChange={(val) => {
                          const newAssumptions = marketRentAssumptions.map((a) =>
                            a.layout === assumption.layout
                              ? { ...a, pf_rent: val === '' ? 0 : Number(val) }
                              : a
                          );
                          setMarketRentAssumptions(newAssumptions);
                        }}
                        size="small"
                        variant="outlined"
                        fullWidth={false}
                        sx={{ minWidth: 120 }}
                        InputProps={{ shrink: "true" }}
                      />
                    </Box>
                  ))}
              </Box> */}

              <UnitTable
                highlightedFields={[]}
                units={units}
                setUnits={setUnits}
                growthRates={growthRates}
                marketRentAssumptions={marketRentAssumptions}
                showMarketRentLayouts={false}
                showMarketRentAssumptions={true}
                setMarketRentAssumptions={setMarketRentAssumptions}
                showGrowthRates={false}
                setGrowthRates={setGrowthRates}
                modelDetails={modelDetails}
                growthRatesOnly={false}
                vacate={true}
                activeStepTitle={steps[activeStep]}
                allowAddUnit={false}
              />

            </Box>
          )}
          {steps[activeStep] === "Residential Rental Units" && (
            <Box sx={{ maxWidth: "1200px", mx: "auto" }}>
                {/* <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>Residential Rental Units</Typography> */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: { xs: 1.5, md: 2.5 }, pt: 2 }}>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  color="primary"
                  value={rentRollMode}
                  onChange={(_e, value) => { if (value) setRentRollMode(value); }}
                >
                  <ToggleButton value="unit">Unit by unit</ToggleButton>
                  <ToggleButton value="mix">By unit mix</ToggleButton>
                  <ToggleButton value="import">Import rent roll</ToggleButton>
                </ToggleButtonGroup>
              </Box>
              {rentRollMode === 'import' ? (
                <RentRollImport
                  units={units}
                  setUnits={setUnits}
                  marketRentAssumptions={marketRentAssumptions}
                  setMarketRentAssumptions={setMarketRentAssumptions}
                  growthRates={growthRates}
                  setGrowthRates={setGrowthRates}
                  modelDetails={modelDetails}
                />
              ) : rentRollMode === 'mix' ? (
                <UnitMixTable
                  units={units}
                  setUnits={setUnits}
                  marketRentAssumptions={marketRentAssumptions}
                  setMarketRentAssumptions={setMarketRentAssumptions}
                  growthRates={growthRates}
                  modelDetails={modelDetails}
                />
              ) : (
                <UnitTable
                  highlightedFields={[ 'layout', 'square_feet', 'current_rent']}
                  units={units}
                  setUnits={setUnits}
                  growthRates={growthRates}
                  marketRentAssumptions={marketRentAssumptions}
                  showMarketRentLayouts={true}
                  showMarketRentAssumptions={false}
                  setMarketRentAssumptions={setMarketRentAssumptions}
                  showGrowthRates={false}
                  setGrowthRates={setGrowthRates}
                  modelDetails={modelDetails}
                  growthRatesOnly={false}
                  vacate={false}
                  activeStepTitle={steps[activeStep]}
                />
              )}

            </Box>
          )}

          {steps[activeStep] === "Rent Assumptions" && selectedModelTypeInfo?.development_model === true && (
            <Box sx={{ maxWidth: "1200px", mx: "auto", px: { xs: 2, md: 0 }, pb: 4 }}>
              <DevelopmentRentalAssumptions
                rows={developmentUnits}
                setRows={setDevelopmentUnits}
                growthRates={growthRates}
                setGrowthRates={setGrowthRates}
              />
            </Box>
          )}

{steps[activeStep] === "Retail Income" && selectedModelTypeInfo?.show_retail === true && selectedModelTypeInfo?.show_rental_units === true && (
            <Box sx={{ maxWidth: "1200px", mx: "auto", px: { xs: 2, md: 0 }, pb: 4 }}>
              <Box sx={{ display: "grid", gap: 3 }}>
                <Box>
                  <RetailSectionHeader
                    title="Base Income"
                    description="Lease terms, rent type, and start months per suite."
                  />
                  <RetailIncomeTable
                    retailIncome={retailIncome}
                    setRetailIncome={setRetailIncome}
                    modelDetails={modelDetails}
                    unitsTotalSqFt={units.reduce((acc, u) => acc + (u.square_feet || 0), 0)}
                    showIndustrialColumns={true}
                  />
                </Box>

                <Box>
                  <RetailSectionHeader
                    title="Recovery Income"
                    description="Recovery timing, pro-rata share, and annual recovery per suite."
                  />
                  <RecoveryIncomeTable expenses={expenses} retailIncome={retailIncome as any[]} setRetailIncome={setRetailIncome as any} />
                </Box>

                <Box>
                  <RetailSectionHeader
                    title="Gross Potential Income"
                    description="Base income plus recoveries, adjusted for vacancy and bad debt."
                  />
                  <GrossPotentialRetailIncomeTable retailIncome={retailIncome as any[]} expenses={expenses} modelDetails={modelDetails} handleFieldChange={handleFieldChange} />
                </Box>

                <Box>
                  <RetailSectionHeader
                    title="Recoverable Operating Expenses"
                    description="Recoverable expenses per SF and totals."
                  />
                  <RetailExpenses expenses={expenses} setExpenses={setExpenses} step={steps[activeStep]} totalRetailSF={retailIncome.reduce((acc, curr) => acc + curr.square_feet, 0)} spaceType={resolvedSpaceType} />

                  <Box sx={{ mt: 2 }}>
                    <RetailSectionHeader
                      title="Expense Inflation"
                      description="Annual growth rates applied to recoverable expenses."
                    />
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", lg: "repeat(3, minmax(0, 1fr))" },
                        gap: 2,
                      }}
                    >
                      {growthRates
                        .filter((rate: any) => rate.type === "retail")
                        .map((rate: any) => (
                          <PercentInput
                            key={rate.name}
                            label={rate.name}
                            value={rate.value}
                            onChange={(e) => {
                              const raw = e.target.value;
                              const newGrowthRates = growthRates.map((r: any) =>
                                r.name === rate.name && r.type === "retail"
                                  ? { ...r, value: raw === "" ? 0 : Number(raw) }
                                  : r
                              );
                              setGrowthRates(newGrowthRates);
                            }}
                            size="small"
                            fullWidth
                          />
                        ))}
                    </Box>
                  </Box>
                </Box>

                <Box>
                  <RetailSectionHeader
                    title="TILC Reserves"
                    description="Assumptions and calculated reserves for new and renewal leases."
                  />
                  <LeasingCostReserves modelDetails={modelDetails} handleFieldChange={handleFieldChange} retailIncome={retailIncome as any[]} />
                </Box>
              </Box>
            </Box>
          )}

          {steps[activeStep] === "Base Income" && selectedModelTypeInfo?.show_retail === true && selectedModelTypeInfo?.show_rental_units === false && (
            <Box sx={{ maxWidth: "1200px", mx: "auto", px: { xs: 2, md: 0 }, pb: 4 }}>
              <RetailSectionHeader
                title="Base Income"
                description="Lease terms, rent type, and start months per suite."
              />
              <RetailIncomeTable
                retailIncome={retailIncome}
                setRetailIncome={setRetailIncome}
                modelDetails={modelDetails}
                unitsTotalSqFt={units.reduce((acc, u) => acc + (u.square_feet || 0), 0)}
                showIndustrialColumns={true}
              />
            </Box>
          )}
          {steps[activeStep] === "Recoverable Operating Expenses" && selectedModelTypeInfo?.show_retail === true && selectedModelTypeInfo?.show_rental_units === false && (
            <Box sx={{ maxWidth: "1200px", mx: "auto", px: { xs: 2, md: 0 }, pb: 4 }}>
              <RetailSectionHeader
                title="Recoverable Operating Expenses"
                description="Recoverable expenses per SF and totals."
              />
              <RetailExpensesIndustrial
                expenses={expenses}
                setExpenses={setExpenses}
                step={steps[activeStep]}
                totalRetailSF={retailIncome.reduce((acc, curr) => acc + curr.square_feet, 0)}
                retailIncome={retailIncome as any[]}
                spaceType={resolvedSpaceType}
              />
              <Box sx={{ mt: 2 }}>
                <RetailSectionHeader
                  title="Expense Inflation"
                  description="Annual growth rates applied to recoverable expenses."
                />
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", lg: "repeat(3, minmax(0, 1fr))" },
                    gap: 2,
                  }}
                >
                  {growthRates
                    .filter((rate: any) => rate.type === "retail")
                    .map((rate: any) => (
                      <PercentInput
                        key={rate.name}
                        label={rate.name}
                        value={rate.value}
                        onChange={(e) => {
                          const raw = e.target.value;
                          const newGrowthRates = growthRates.map((r: any) =>
                            r.name === rate.name && r.type === "retail"
                              ? { ...r, value: raw === "" ? 0 : Number(raw) }
                              : r
                          );
                          setGrowthRates(newGrowthRates);
                        }}
                        size="small"
                        fullWidth
                      />
                    ))}
                </Box>
              </Box>
            </Box>
          )}
          {steps[activeStep] === "Recovery and Gross Potential Income" && selectedModelTypeInfo?.show_retail === true && selectedModelTypeInfo?.show_rental_units === false && (
            <Box sx={{ maxWidth: "1200px", mx: "auto", px: { xs: 2, md: 0 }, pb: 4 }}>
              <Box sx={{ display: "grid", gap: 3 }}>
                <Box>
                  <RetailSectionHeader
                    title="Recovery Income"
                    description="Recovery timing, pro-rata share, and annual recovery per suite."
                  />
                  <RecoveryIncomeTableIndustrial retailIncome={retailIncome as any[]} setRetailIncome={setRetailIncome as any} expenses={expenses} />
                </Box>
                <Box>
                  <RetailSectionHeader
                    title="Gross Potential Income"
                    description="Base income plus recoveries, adjusted for vacancy and bad debt."
                  />
                  <GrossPotentialRetailIncomeTableIndustrial retailIncome={retailIncome as any[]} expenses={expenses} modelDetails={modelDetails} handleFieldChange={handleFieldChange} />
                </Box>
              </Box>
            </Box>
          )}
          {steps[activeStep] === "TILC Reserves" && selectedModelTypeInfo?.show_retail === true && selectedModelTypeInfo?.show_rental_units === false && (
            <Box sx={{ maxWidth: "1200px", mx: "auto", px: { xs: 2, md: 0 }, pb: 4 }}>
              <RetailSectionHeader
                title="TILC Reserves"
                description="Assumptions and calculated reserves for new and renewal leases."
              />
              <LeasingCostReserves modelDetails={modelDetails} handleFieldChange={handleFieldChange} retailIncome={retailIncome as any[]} />
            </Box>
          )}
          {steps[activeStep] === "Income Summary" && selectedModelTypeInfo?.show_retail === true && selectedModelTypeInfo?.show_rental_units === false && (
            <Box sx={{ maxWidth: "1200px", mx: "auto", p:2 }}>
              <RetailSummary retailIncome={retailIncome as any[]} expenses={expenses} modelDetails={modelDetails} />
            </Box>
          )}
          {/* {steps[activeStep] === "Retail Income" && selectedModelTypeInfo?.show_retail === true && selectedModelTypeInfo?.show_rental_units === false && (
            <Box sx={{ maxWidth: "1200px", mx: "auto", p:2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: "bolder", fontSize: { xs: "1rem", sm: "1.125rem" } }}>Base Retail Income</Typography>

              <RetailIncomeTable
                retailIncome={retailIncome}
                setRetailIncome={setRetailIncome}
                modelDetails={modelDetails}
                unitsTotalSqFt={units.reduce((acc, u) => acc + (u.square_feet || 0), 0)}
                showIndustrialColumns={true}
              />
              
              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle1" sx={{ fontWeight: "bolder", mt: 2, mb: 2, fontSize: { xs: "1rem", sm: "1.125rem" } }}>Recovery Income</Typography>

                  <RecoveryIncomeTableIndustrial expenses={expenses} retailIncome={retailIncome as any[]} setRetailIncome={setRetailIncome as any} />
          
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: "bolder", mt: 2, mb: 0, fontSize: { xs: "1rem", sm: "1.125rem" } }}>Gross Potential Retail Income</Typography>
                  <GrossPotentialRetailIncomeTableIndustrial retailIncome={retailIncome as any[]} expenses={expenses} modelDetails={modelDetails} handleFieldChange={handleFieldChange} />

                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: "bolder", mt: 2, mb: 2, fontSize: { xs: "1rem", sm: "1.125rem", mt: 2  } }}>Recoverable Retail Operating Expenses</Typography>


                  <RetailExpensesIndustrial
                    expenses={expenses}
                    setExpenses={setExpenses}
                    step={steps[activeStep]}
                    totalRetailSF={retailIncome.reduce((acc, curr) => acc + curr.square_feet, 0)}
                    retailIncome={retailIncome as any[]}
                  />

                <Box sx={{ mt: 2, width: '100%' }}>
                {growthRates
                  .filter((rate: any) => rate.type === 'retail')
                  .map((rate: any, idx: number) => (
                    <Box
                      key={idx}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        mb: 1,
                        width: '100%',
                      }}
                    >
                      <Box sx={{ flex: '0 0 auto', fontWeight: 500, color: 'text.secondary', pr: 2, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {rate.name} (%)
                      </Box>
                      <TextField
                        type="number"
                        value={rate.value}
                        onChange={(e) => {
                          const newGrowthRates = growthRates.map((r: any) =>
                            r.name === rate.name && r.type === 'retail'
                              ? { ...r, value: Number(e.target.value) }
                              : r
                          );
                          setGrowthRates(newGrowthRates);
                        }}
                        size="small"
                        sx={{ flex: 1, minWidth: 120 }}
                        inputProps={{ style: { textAlign: 'right' } }}
                        variant="outlined"
                        fullWidth
                      />
                    </Box>
                  ))}
              </Box>

              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: "bolder", mt: 2, mb: 2, fontSize: { xs: "1rem", sm: "1.125rem", mt: 2  } }}>Calculate the Leasing Cost Reserves</Typography>
                  <LeasingCostReserves modelDetails={modelDetails} handleFieldChange={handleFieldChange} retailIncome={retailIncome as any[]} />
            </Box>
          )} */}

          {EXPENSE_STEPS.includes(steps[activeStep]) && (
            <Box sx={{ maxWidth: "1200px", mx: "auto", p:2 }}>
              <Expenses 
                operatingExpenses={operatingExpenses} 
                variables={variables} 
                modelDetails={modelDetails} 
                expenses={expenses} 
                setExpenses={setExpenses} 
                step={steps[activeStep]} 
                units={units} 
                amenityIncome={amenityIncome} 
                retailIncome={retailIncome} 
                retailExpenses={expenses.filter((expense: any) => expense.type === "Retail")} 
                industrialModel={selectedModelTypeInfo?.show_rental_units === false && selectedModelTypeInfo?.show_retail === true}
                defaultShowMonths={selectedModelTypeInfo?.development_model === true}
                showBuildableSQFt={steps[activeStep] === "Hard Costs"}
                modelType={selectedModelTypeInfo}
                buildableSQFt={steps[activeStep] === "Hard Costs"
                  ? resolveBuildableSqFt(
                      modelDetails,
                      selectedModelTypeInfo?.development_model ? developmentUnits : [],
                    )
                  : 0}
                />
              {/* <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>{steps[activeStep]}</Typography> */}

            </Box>
          )}

{steps[activeStep] === "Soft Costs" && (
            <Box sx={{ maxWidth: "1200px", mx: "auto", p:2 }}>
              <Expenses 
                operatingExpenses={operatingExpenses} 
                variables={variables} 
                modelDetails={modelDetails} 
                expenses={expenses} 
                modelType={selectedModelTypeInfo}
                setExpenses={setExpenses} 
                step={"Soft Costs"} 
                units={units} 
                amenityIncome={amenityIncome} 
                retailIncome={retailIncome} 
                retailExpenses={expenses.filter((expense: any) => expense.type === "Retail")} 
                industrialModel={selectedModelTypeInfo?.show_rental_units === false && selectedModelTypeInfo?.show_retail === true}
                defaultShowMonths={selectedModelTypeInfo?.development_model === true}
                showBuildableSQFt={false}
                buildableSQFt={selectedModelTypeInfo?.development_model === true
                  ? resolveBuildableSqFt(modelDetails, developmentUnits)
                  : 0}
                />
              {/* <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>{steps[activeStep]}</Typography> */}

            </Box>
          )}
          {steps[activeStep] === "Amenity Income" && (
            <Box sx={{ maxWidth: "1200px", mx: "auto" , p:2}}>

              <AmenityIncomeTable amenityIncome={amenityIncome} setAmenityIncome={setAmenityIncome} modelUnitCount={units.length} />
              <Box sx={{ mt: 2, width: '100%' }}>
                {growthRates
                  .filter((rate: any) => rate.type === 'amenity')
                  .map((rate: any, idx: number) => (
                    <Box
                      key={idx}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        mb: 1,
                        width: '100%',
                      }}
                    >
                      <Box sx={{ flex: '0 0 auto', fontWeight: 500, color: 'text.secondary', pr: 2, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {rate.name} (%)
                      </Box>
                      <TextField
                        type="number"
                        value={rate.value}
                        onChange={(e) => {
                          const newGrowthRates = growthRates.map((r: any) =>
                            r.name === rate.name && r.type === 'amenity'
                              ? { ...r, value: Number(e.target.value) }
                              : r
                          );
                          setGrowthRates(newGrowthRates);
                        }}
                        size="small"
                        sx={{ flex: 1, minWidth: 120 }}
                        inputProps={{ style: { textAlign: 'right' } }}
                        variant="outlined"
                        fullWidth
                      />
                    </Box>
                  ))}
              </Box>

            </Box>
          )}
          {steps[activeStep] === "Operating Expenses" && (
            <Box sx={{ maxWidth: "1200px", mx: "auto", p:2 }}>
{selectedModelTypeInfo?.development_model === true && (

              <OperatingExpensesTable 
              operatingExpenses={operatingExpenses} 
              setOperatingExpenses={setOperatingExpenses} 
              units={units} 
              developmentUnits={developmentUnits}
              isDevelopmentModel={true}
              amenityIncome={amenityIncome} 
              modelDetails={modelDetails} 
              retailIncome={retailIncome} 
              retailExpenses={expenses.filter((expense: any) => expense.type === "Retail")} />
)}

{selectedModelTypeInfo?.development_model !== true && (

<OperatingExpensesTable 
operatingExpenses={operatingExpenses} 
setOperatingExpenses={setOperatingExpenses} 
units={units} 
developmentUnits={developmentUnits}
isDevelopmentModel={false}
amenityIncome={amenityIncome} 
modelDetails={modelDetails} 
retailIncome={retailIncome} 
retailExpenses={expenses.filter((expense: any) => expense.type === "Retail")} />
)}

              <Box sx={{ mt: 2, width: '100%' }}>
                {growthRates
                  .filter((rate: any) => rate.type === 'expense')
                  .map((rate: any, idx: number) => (
                    <Box
                      key={idx}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        mb: 1,
                        width: '100%',
                      }}
                    >
                      <Box sx={{ flex: '0 0 auto', fontWeight: 500, color: 'text.secondary', pr: 2, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {rate.name} (%)
                      </Box>
                      <TextField
                        type="number"
                        value={rate.value}
                        onChange={(e) => {
                          const newGrowthRates = growthRates.map((r: any) =>
                            r.name === rate.name && r.type === 'expense'
                              ? { ...r, value: Number(e.target.value) }
                              : r
                          );
                          setGrowthRates(newGrowthRates);
                        }}
                        size="small"
                        sx={{ flex: 1, minWidth: 120 }}
                        inputProps={{ style: { textAlign: 'right' } }}
                        variant="outlined"
                        fullWidth
                      />
                    </Box>
                  ))}
              </Box>

            </Box>
          )}

          {steps[activeStep] === "Refinancing" && (
            <>
              {selectedModelTypeInfo?.development_model === true && (
                <DevelopmentRefinancingSection modelDetails={modelDetails} handleFieldChange={handleFieldChange} finalMetricsCalculating={finalMetricsCalculating} variables={variables} />
              )}
              {selectedModelTypeInfo?.development_model !== true && (
                <RefinancingSection modelDetails={modelDetails} handleFieldChange={handleFieldChange} finalMetricsCalculating={finalMetricsCalculating} variables={variables} />
              )}
            </>
          )}

          {steps[activeStep] === "Senior Construction Loan Financing" && (
            <SeniorConstructionLoanSection modelDetails={modelDetails} handleFieldChange={handleFieldChange} finalMetricsCalculating={finalMetricsCalculating} variables={variables} expenses={expenses} />
          )}

          {steps[activeStep] === "Second Lien" && (
            <SecondLienSection modelDetails={modelDetails} handleFieldChange={handleFieldChange} finalMetricsCalculating={finalMetricsCalculating} variables={variables} expenses={expenses} />
          )}

          {steps[activeStep] === "Net Operating Income" && (
            <Box sx={{ maxWidth: "1200px", mx: "auto", p:2 }}>
              <NetOperatingIncomeTable finalMetricsCalculating={finalMetricsCalculating} noiTableValues={noiTableValues} />
            </Box>
          )}

          {steps[activeStep] === "Acquisition Financing" && (
            <>
              <AcquisitionFinancingSection modelDetails={modelDetails} handleFieldChange={handleFieldChange} finalMetricsCalculating={finalMetricsCalculating} variables={variables} />
            </>
          )}

          {steps[activeStep] === "Leasing Assumptions" && (
            <Box sx={{ maxWidth: "1200px", mx: "auto", p:0 }}>
              <LeasingAssumptions
                modelDetails={modelDetails}
                handleFieldChange={handleFieldChange}
                expenses={expenses}
                developmentUnits={developmentUnits}
                developmentModel={selectedModelTypeInfo?.development_model ?? false}
              />
            </Box>
          )}


{steps[activeStep] === "Exit Assumptions" && (
  <Box sx={{ maxWidth: "1200px", mx: "auto", p: 0 }}>
    <ExitAssumptions
      modelDetails={modelDetails}
      handleFieldChange={handleFieldChange}
      showRetail={selectedModelTypeInfo?.show_retail}
      showRentalUnits={selectedModelTypeInfo?.show_rental_units}
      variables={variables}
      numUnits={exitAssumptionsNumUnits}
      developmentModel={selectedModelTypeInfo?.development_model}
      finalMetricsCalculating={finalMetricsCalculating}
    />
  </Box>
)}
 
 
          {/* Show "General Property Assumptions" section at step 1, all others at the end */}
          {selectedModelTypeInfo?.sections.map((section, index) => {
            // Show "General Property Assumptions" at step 1
            if (
              section.name === "General Property Assumptions" &&
              activeStep === steps.indexOf("General Property Assumptions")
            ) {
              return (
                <Box key={section.id}>
                  {/* Content Area */}
                  <Box sx={{ maxWidth: 800, mx: 'auto', px: { xs: 2, sm: 4 }, pb: 4 }}>
                  <ContentCard>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      {section.fields.filter(field => field.active === true).map((field: Field) => {
                    const fieldValue = modelDetails.user_model_field_values.find((fv: any) => fv.field_id === field.id)?.value || '';
                    const startMonth = modelDetails.user_model_field_values.find((fv: any) => fv.field_id === field.id)?.start_month ?? '';
                    const endMonth = modelDetails.user_model_field_values.find((fv: any) => fv.field_id === field.id)?.end_month ?? '';
 
                    return (
                      <SectionFields key={field.id} field={field} fieldValue={fieldValue} startMonth={startMonth} endMonth={endMonth} handleFieldChange={handleFieldChange} />
 
                    );
                      })}
                    </Box>
                  </ContentCard>
                  </Box>
                </Box>
              );
            }


            // Only show the current section that matches the current step
            if (
              steps[activeStep] === section.name && section.name !== "General Property Assumptions"
              && section.name !== "Acquisition Financing"
              && section.name !== "Refinancing"
              && section.name !== "Senior Construction Loan Financing"
              && section.name !== "Second Lien"
              && section.name !== "Leasing Assumptions"
              && section.name !== "Exit Assumptions"
            ) {
              // Calculate the last step for finish button
              const lastStep = steps.length - 1;
              const thisStep = activeStep;
 
              return (
                <Box key={section.id} sx={{ p: 2, borderRadius: 2, border: "1px solid #e6e9ef", maxWidth: "1200px", mx: "auto" }}>
                  {/* <Box key={section.id} sx={{ mt: 0, backgroundColor: "white", p: 2, borderRadius: 2, maxWidth: "600px", mx: "auto" }}> */}
               
                  {section.fields.filter(field => field.active === true).map((field: Field) => {
                    const fieldValue = modelDetails.user_model_field_values.find((fv: any) => fv.field_id === field.id)?.value || '';
                    const startMonth = modelDetails.user_model_field_values.find((fv: any) => fv.field_id === field.id)?.start_month ?? '';
                    const endMonth = modelDetails.user_model_field_values.find((fv: any) => fv.field_id === field.id)?.end_month ?? '';
 
                    return (
                      <SectionFields key={field.id} field={field} fieldValue={fieldValue} startMonth={startMonth} endMonth={endMonth} handleFieldChange={handleFieldChange} />
                    );
                  })}
 
                {/* </Box> */}
    </Box>               
              );
            }
 
 
            // Otherwise, don't render anything for this section at this step
            return null;
          })}
          



          {nameError && (
            <Typography color="error" sx={{ mt: 2 }}>
              Duplicate names are not allowed.
            </Typography>
          )}
          {rentalRateError && (
            <Typography color="error" sx={{ mt: 2 }}>
              At least one rental unit growth rate is required.
            </Typography>
          )}
          </Box>
        </ModelStepper>
      )}
    </>
  );
};

export default CreateModel;
